import type { ArtworkMode, ArtworkState, UploadRejectionCode } from "./artwork-controller";
import type { ConfiguratorField, ConfiguratorState, FieldError } from "./configurator-controller";
import { getDisplayedError } from "./configurator-controller";
import type { OrderLineState, OrderMutationConflict } from "./order-line-controller";
import type { PricePreview, PricingState } from "./pricing-controller";

export type PvcMaterialPresentation = {
  id: string;
  label: string;
  description?: string;
};

export type PvcAddonPresentation = {
  id: string;
  label: string;
  description?: string;
};

export type PvcArtworkOptionPresentation = {
  mode: ArtworkMode;
  label: string;
  description?: string;
  available: boolean;
};

export type PvcGalleryItem = {
  id: string;
  src: string;
  alt: string;
  label?: string;
};

export type PvcProductPresentation = {
  title: string;
  description?: string;
  proofRequired: boolean;
  materials: PvcMaterialPresentation[];
  addons: PvcAddonPresentation[];
  artworkOptions: PvcArtworkOptionPresentation[];
  gallery?: PvcGalleryItem[];
};

export type PriceDisplayModel =
  | { kind: "none"; message?: string }
  | { kind: "skeleton" }
  | { kind: "value"; quote: PricePreview; recalculating: boolean; stale: boolean; staleMessage?: string };

export function buildPriceDisplay(state: PricingState): PriceDisplayModel {
  switch (state.status) {
    case "initial-loading":
      return { kind: "skeleton" };
    case "ready":
      return { kind: "value", quote: state.quote, recalculating: false, stale: false };
    case "recalculating":
      return { kind: "value", quote: state.quote, recalculating: true, stale: false };
    case "stale":
      return {
        kind: "value",
        quote: state.quote,
        recalculating: false,
        stale: true,
        staleMessage: pricingFailureMessage(state.error.code),
      };
    case "error":
      return { kind: "none", message: pricingFailureMessage(state.error.code) };
    case "rejected":
      return { kind: "none", message: "יש לעדכן את ההגדרות כדי לקבל מחיר." };
    case "idle":
      return { kind: "none" };
  }
}

export function pricingFailureMessage(code: string): string {
  switch (code) {
    case "OFFLINE": return "אין חיבור לאינטרנט. המחיר יעודכן כשהחיבור יחזור.";
    case "QUOTE_EXPIRED": return "תוקף המחיר פג. מחשבים מחיר מעודכן.";
    case "SCHEMA_CHANGED": return "אפשרויות המוצר השתנו. מחשבים מחיר מחדש.";
    case "SESSION_CHANGED": return "הסל השתנה. מחשבים מחיר מחדש.";
    case "NETWORK": return "לא הצלחנו לעדכן את המחיר כרגע.";
    default: return "לא הצלחנו לחשב מחיר כרגע.";
  }
}

export function fieldErrorMessage(
  field: ConfiguratorField,
  error: FieldError,
  constraints: {
    minWidthCm: number; maxWidthCm: number;
    minHeightCm: number; maxHeightCm: number;
    stepCm: number; minQuantity: number; maxSelfServeQuantity: number;
  }
): string {
  if (error.message) return error.message;

  switch (error.code) {
    case "REQUIRED":
      if (field === "widthCm") return "יש להזין רוחב.";
      if (field === "heightCm") return "יש להזין גובה.";
      if (field === "quantity") return "יש להזין כמות.";
      return "יש להשלים את השדה.";
    case "BELOW_MIN":
      return field === "widthCm"
        ? `הרוחב המינימלי הוא ${constraints.minWidthCm} ס״מ.`
        : `הגובה המינימלי הוא ${constraints.minHeightCm} ס״מ.`;
    case "ABOVE_MAX":
      return field === "widthCm"
        ? `הרוחב המרבי להזמנה באתר הוא ${constraints.maxWidthCm} ס״מ.`
        : `הגובה המרבי להזמנה באתר הוא ${constraints.maxHeightCm} ס״מ.`;
    case "INVALID_STEP": return `ניתן להזין מידות בקפיצות של ${constraints.stepCm} ס״מ.`;
    case "QUANTITY_INVALID": return "יש להזין כמות שלמה.";
    case "QUANTITY_TOO_LOW": return `הכמות המינימלית היא ${constraints.minQuantity}.`;
    case "QUANTITY_EXCEEDS_SELF_SERVE": return `להזמנה של יותר מ־${constraints.maxSelfServeQuantity} יחידות נציע הצעת מחיר מותאמת.`;
    case "MATERIAL_UNAVAILABLE": return "החומר שבחרתם אינו זמין כרגע. בחרו חומר אחר.";
    case "DATE_INVALID_FORMAT": return "יש לבחור תאריך תקין.";
    case "DATE_UNAVAILABLE": return "לא ניתן להכין את ההזמנה לתאריך שבחרתם.";
    default: return "הערך שהוזן אינו זמין להזמנה.";
  }
}

export function displayedFieldError(
  state: ConfiguratorState,
  field: ConfiguratorField,
  constraints: Parameters<typeof fieldErrorMessage>[2]
): string | null {
  const error = getDisplayedError(state, field);
  return error ? fieldErrorMessage(field, error, constraints) : null;
}

export function uploadRejectionMessage(code: UploadRejectionCode): string {
  switch (code) {
    case "UNSUPPORTED_FILE_TYPE": return "סוג הקובץ אינו נתמך.";
    case "FILE_TOO_LARGE": return "הקובץ גדול מהמותר.";
    case "CORRUPT_FILE": return "לא הצלחנו לקרוא את הקובץ.";
    case "FILE_SIGNATURE_MISMATCH": return "תוכן הקובץ אינו תואם לסוג שלו.";
    case "FILE_VALIDATION_FAILED": return "הקובץ לא עבר את בדיקת ההדפסה.";
    case "PROCESSING_FAILED": return "אירעה שגיאה בעיבוד הקובץ.";
  }
}

export function artworkStatusMessage(state: ArtworkState): string | null {
  if (state.file) return "הקובץ התקבל.";
  if (state.directUpload.status === "uploading") return "הקובץ עולה…";
  if (state.directUpload.status === "rejected") return uploadRejectionMessage(state.directUpload.code);
  if (state.directUpload.status === "error") return "העלאת הקובץ נכשלה. אפשר לנסות שוב או לשלוח אחר כך.";
  if (state.uploadSession?.status === "processing") return "הקובץ התקבל במחשב ונמצא בבדיקה.";
  if (state.uploadSession?.status === "rejected") return "הקובץ שהועלה לא עבר את הבדיקה. יש לנסות שוב.";
  if (state.uploadSession?.status === "expired") return "הקישור להעלאה פג תוקף.";
  if (state.uploadSession?.status === "failed") return "לא הצלחנו לבדוק את ההעלאה מהמחשב.";
  if (state.uploadSession?.status === "manual-check") return "עדיין מחכים לקובץ. אפשר לבדוק את הסטטוס ידנית.";
  return null;
}

export function conflictMessage(conflict: OrderMutationConflict): string {
  switch (conflict.code) {
    case "PRICE_CHANGED": return "המחיר עודכן. בדקו את המחיר החדש ואשרו שוב את ההוספה לסל.";
    case "CART_SESSION_EXPIRED": return "תוקף הסל פג. צריך לשחזר את הסל ולחשב מחיר מחדש.";
    case "CART_OWNERSHIP_CONFLICT": return "כבר קיימים פריטים בסל של החשבון. בחרו עם איזה סל להמשיך.";
    case "ORDER_LINE_VERSION_CONFLICT": return "הפריט השתנה מאז שפתחתם אותו לעריכה. יש לטעון את הגרסה העדכנית.";
    case "PRODUCT_SCHEMA_CHANGED": return "אפשרויות המוצר עודכנו. יש לרענן את פרטי המוצר לפני ההמשך.";
    case "ARTWORK_FILE_INVALID": return "הקובץ כבר אינו זמין. יש להעלות אותו מחדש.";
    case "DATE_UNAVAILABLE": return "התאריך שבחרתם כבר אינו זמין.";
    case "OPTION_UNAVAILABLE": return "אחת האפשרויות שבחרתם אינה זמינה יותר.";
    case "CONFIGURATION_INVALID": return "חלק מהגדרות המוצר אינן תקינות.";
  }
}

export function submitLabel(orderState: OrderLineState, mode: "create" | "edit" = "create"): string {
  if (orderState.status === "submitting") return orderState.operation === "update" ? "מעדכן…" : "מוסיף לסל…";
  if (orderState.status === "success") return orderState.operation === "update" ? "✓ הפריט עודכן" : "הוספת שלט נוסף";
  if (orderState.status === "conflict" && orderState.conflict.code === "PRICE_CHANGED") return "להוסיף לסל במחיר החדש";
  return mode === "edit" ? "עדכון הפריט" : "הוספה לסל";
}

export function isOrderBusy(state: OrderLineState): boolean {
  return state.status === "submitting" || state.status === "recovering" || state.status === "recovering-cart-session" || state.status === "resolving-cart-ownership";
}

export function formatMoney(minor: number, currencyCode = "ILS"): string {
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

export function formatDate(date: string | null): string | null {
  if (!date) return null;
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return date;
  return new Intl.DateTimeFormat("he-IL", { day: "numeric", month: "numeric", year: "numeric" })
    .format(new Date(Date.UTC(year, month - 1, day)));
}

export function localAreaSqm(state: ConfiguratorState): number | null {
  const width = state.width.value;
  const height = state.height.value;
  if (width === null || height === null || width <= 0 || height <= 0) return null;
  return (width / 100) * (height / 100);
}
